import { FC } from "react";

import { roleIcon, roleLabel } from "./roles";

import { IconText } from "mds/components/IconText";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { useHomeRole } from "providers/HomeRoleProvider";
import { useAppSelector } from "store/index";
import { selectAvailableHomeRoles } from "store/selectors";
import { GENERAL_EVENT_CATEGORY } from "utils/amplitude";
import { getDefaultHomeRole } from "utils/home-role";
import { useNavigate } from "utils/routing";
import { type HomeRole, homeUrlForRole } from "utils/urls";

export type RoleSelectProps = {
  className?: string;
  isLoading?: boolean;
};

type RoleListboxOption = ListboxOption & { id: HomeRole };

export const RoleSelect: FC<RoleSelectProps> = ({ className, isLoading }) => {
  const navigate = useNavigate();
  const homeRole = useHomeRole();
  const availableRoles = useAppSelector(selectAvailableHomeRoles);
  const displayRole = homeRole ?? getDefaultHomeRole(availableRoles);
  const hasChoices = availableRoles.length > 1;

  if (!isLoading && !hasChoices) {
    return (
      <IconText
        className="text-sm font-medium text-black-tint-20"
        iconStart={roleIcon[displayRole]}
        text={roleLabel[displayRole]}
      />
    );
  }

  const rolesForItems = hasChoices ? availableRoles : [displayRole];
  const items: RoleListboxOption[] = rolesForItems.map((role) => ({
    id: role,
    title: roleLabel[role],
    iconStart: roleIcon[role],
  }));

  return (
    <Listbox
      buttonKind="secondary"
      buttonSize="s"
      className={className}
      disabled={isLoading}
      eventName="New user role selected"
      eventProperties={GENERAL_EVENT_CATEGORY}
      eventPropertyKey="newRole"
      items={items}
      openMenuEventName="Switch User role"
      selectedValue={displayRole}
      onSelectionChange={(item) => {
        navigate(homeUrlForRole(item.id));
      }}
    />
  );
};
